import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface ProductItem {
  id: number;
  name: string;
  description: string | null;
  category_id: number | null;
  status: string;
  price: number | null;
  currency: string;
  discount_enabled: boolean;
  discount_price: number | null;
  created_at: string;
  pdf_url?: string | null;
  artista?: string | null;
  album?: string | null;
  musica?: string | null;
  asset_id?: number | null;
}

interface Category {
  id: number;
  name: string;
}

type Modo = "lista" | "novo" | "editar";

// Extrai nome limpo do arquivo PDF (remove extensão e underscores)
function extrairNomeDoPdf(fileName: string): string {
  return fileName
    .replace(/\.pdf$/i, "")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function PartituraManager() {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterArtista, setFilterArtista] = useState("");
  const [modo, setModo] = useState<Modo>("lista");
  const [editing, setEditing] = useState<ProductItem | null>(null);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    artista: "",
    album: "",
    description: "",
    category_id: "",
    status: "active",
    price: "",
    currency: "BRL",
    discount_enabled: false,
    discount_price: "",
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    const [{ data: prodData }, { data: catData }, { data: assetData }] = await Promise.all([
      supabase.from("product_items").select("*").order("id", { ascending: false }).limit(1000),
      supabase.from("product_categories").select("id, name").order("sort_order"),
      supabase.from("digital_assets").select("id, product_id, pdf_url, artista, album, musica").limit(1000),
    ]);
    if (catData) setCategories(catData as Category[]);
    if (prodData) {
      const enriched = (prodData as ProductItem[]).map((p) => {
        const asset = (assetData || []).find((a: any) => a.product_id === p.id);
        return {
          ...p,
          pdf_url: asset?.pdf_url || null,
          artista: asset?.artista || null,
          album: asset?.album || null,
          musica: asset?.musica || null,
          asset_id: asset?.id || null,
        };
      });
      setProducts(enriched);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Quando seleciona PDF, preenche automaticamente o nome da música
  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] || null;
    setUploadFile(file);
    if (file && !form.name) {
      const nomeExtraido = extrairNomeDoPdf(file.name);
      setForm((prev) => ({ ...prev, name: nomeExtraido }));
    }
  }

  function resetForm() {
    setForm({
      name: "", artista: "", album: "", description: "",
      category_id: "", status: "active", price: "",
      currency: "BRL", discount_enabled: false, discount_price: "",
    });
    setUploadFile(null);
    setErro("");
    setMensagem("");
  }

  function openNovo() {
    setEditing(null);
    resetForm();
    setModo("novo");
  }

  function openEditar(p: ProductItem) {
    setEditing(p);
    setForm({
      name: p.name,
      artista: p.artista || "",
      album: p.album || "",
      description: p.description || "",
      category_id: p.category_id ? String(p.category_id) : "",
      status: p.status,
      price: p.price != null ? String(p.price) : "",
      currency: p.currency || "BRL",
      discount_enabled: p.discount_enabled || false,
      discount_price: p.discount_price != null ? String(p.discount_price) : "",
    });
    setUploadFile(null);
    setErro("");
    setMensagem("");
    setModo("editar");
  }

  async function handlePublicar(e: React.FormEvent) {
    e.preventDefault();
    setErro("");
    setMensagem("");

    if (!uploadFile) { setErro("Selecione um arquivo PDF."); return; }
    if (!form.name.trim()) { setErro("O nome da música é obrigatório."); return; }
    if (!form.artista.trim()) { setErro("O nome do artista é obrigatório."); return; }
    if (!form.price || Number(form.price) <= 0) { setErro("Informe o preço."); return; }

    setSaving(true);
    setUploading(true);
    try {
      const { data: newProduct, error: prodError } = await supabase
        .from("product_items")
        .insert({
          name: form.name.trim(),
          description: form.description.trim() || null,
          category_id: form.category_id ? Number(form.category_id) : null,
          status: form.status,
          price: Number(form.price),
          currency: form.currency,
          stock: 999,
          discount_enabled: form.discount_enabled,
          discount_price: form.discount_enabled && form.discount_price ? Number(form.discount_price) : null,
        })
        .select("id")
        .single();

      if (prodError || !newProduct) throw new Error(prodError?.message || "Erro ao criar partitura.");

      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("fileName", uploadFile.name);
      formData.append("productId", String(newProduct.id));

      const { data: uploadData, error: uploadError } = await supabase.functions.invoke("upload-partitura", { body: formData });
      if (uploadError || !uploadData) throw new Error(uploadError?.message || "Erro no upload do PDF.");
      if (uploadData.error) throw new Error(uploadData.error);

      const { error: assetError } = await supabase.from("digital_assets").insert({
        product_id: newProduct.id,
        pdf_url: uploadData.url,
        file_name: uploadFile.name,
        artista: form.artista.trim(),
        album: form.album.trim() || null,
        musica: form.name.trim(),
      });
      if (assetError) throw new Error(assetError.message);

      setMensagem("Partitura publicada com sucesso!");
      resetForm();
      setModo("lista");
      loadData();
    } catch (err: any) {
      setErro(err.message || "Erro ao publicar partitura.");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  }

  async function handleSalvarEdicao(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setErro("");
    setMensagem("");
    setSaving(true);
    try {
      const { error: prodError } = await supabase
        .from("product_items")
        .update({
          name: form.name.trim(),
          description: form.description.trim() || null,
          category_id: form.category_id ? Number(form.category_id) : null,
          status: form.status,
          price: form.price ? Number(form.price) : null,
          currency: form.currency,
          discount_enabled: form.discount_enabled,
          discount_price: form.discount_enabled && form.discount_price ? Number(form.discount_price) : null,
        })
        .eq("id", editing.id);
      if (prodError) throw new Error(prodError.message);

      let pdfUrl = editing.pdf_url;

      if (uploadFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", uploadFile);
        formData.append("fileName", uploadFile.name);
        formData.append("productId", String(editing.id));
        const { data: uploadData, error: uploadError } = await supabase.functions.invoke("upload-partitura", { body: formData });
        if (uploadError || !uploadData) throw new Error(uploadError?.message || "Erro no upload.");
        if (uploadData.error) throw new Error(uploadData.error);
        pdfUrl = uploadData.url;
        setUploading(false);
      }

      if (editing.asset_id) {
        const { error: assetError } = await supabase
          .from("digital_assets")
          .update({
            artista: form.artista.trim() || null,
            album: form.album.trim() || null,
            musica: form.name.trim(),
            ...(uploadFile ? { pdf_url: pdfUrl, file_name: uploadFile.name } : {}),
          })
          .eq("id", editing.asset_id);
        if (assetError) throw new Error(assetError.message);
      } else if (pdfUrl) {
        const { error: assetError } = await supabase.from("digital_assets").insert({
          product_id: editing.id,
          pdf_url: pdfUrl,
          file_name: uploadFile?.name || null,
          artista: form.artista.trim() || null,
          album: form.album.trim() || null,
          musica: form.name.trim(),
        });
        if (assetError) throw new Error(assetError.message);
      }

      setMensagem("Partitura atualizada com sucesso!");
      setModo("lista");
      setEditing(null);
      resetForm();
      loadData();
    } catch (err: any) {
      setErro(err.message || "Erro ao salvar alterações.");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Excluir esta partitura?")) return;
    setLoading(true);
    const { error } = await supabase.from("product_items").delete().eq("id", id);
    if (error) setErro(error.message);
    else { setMensagem("Partitura excluída."); loadData(); }
    setLoading(false);
  }

  // Artistas únicos para o filtro
  const artistas = Array.from(new Set(products.map((p) => p.artista).filter(Boolean) as string[])).sort();

  const filtered = products.filter((p) => {
    const matchSearch = !search.trim() ||
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.artista || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus ? p.status === filterStatus : true;
    const matchArtista = filterArtista ? p.artista === filterArtista : true;
    return matchSearch && matchStatus && matchArtista;
  });

  // Agrupa por artista para exibição
  const agrupados = filtered.reduce((acc, p) => {
    const artista = p.artista || "Sem artista";
    if (!acc[artista]) acc[artista] = [];
    acc[artista].push(p);
    return acc;
  }, {} as Record<string, ProductItem[]>);

  const categoryName = (id: number | null) => categories.find((c) => c.id === id)?.name || "—";
  const formatPrice = (val: number | null) => val == null ? "—" : `R$ ${val.toFixed(2).replace(".", ",")}`;

  // ===== FORMULÁRIO =====
  if (modo === "novo" || modo === "editar") {
    const isEdicao = modo === "editar";
    return (
      <div>
        <button
          onClick={() => { setModo("lista"); resetForm(); setEditing(null); }}
          className="flex items-center gap-2 text-godoy-gold hover:text-godoy-goldlight text-sm mb-6 transition-colors"
        >
          <i className="ri-arrow-left-line" /> Voltar para lista
        </button>

        <h3 className="font-serif text-xl font-semibold text-godoy-cream mb-1">
          {isEdicao ? `Editar: ${editing?.name}` : "Nova Partitura"}
        </h3>
        {isEdicao && (
          <p className="text-godoy-lightgray text-xs mb-6">
            Altere as informações. Envie um novo PDF apenas se quiser substituir o atual.
          </p>
        )}

        {mensagem && <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-emerald-400 text-sm">{mensagem}</div>}
        {erro && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">{erro}</div>}

        <form onSubmit={isEdicao ? handleSalvarEdicao : handlePublicar} className="space-y-6">

          {/* PDF */}
          <div className="p-5 bg-godoy-black/40 border border-godoy-gray/20 rounded-lg">
            <h4 className="text-godoy-gold text-sm font-semibold mb-3 flex items-center gap-2">
              <i className="ri-file-pdf-line" /> Arquivo PDF
            </h4>
            {isEdicao && editing?.pdf_url && (
              <div className="mb-3 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-md flex items-center gap-2">
                <i className="ri-check-line text-emerald-400" />
                <a href={editing.pdf_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 text-xs hover:underline truncate">
                  PDF atual: {editing.pdf_url.split("/").pop()}
                </a>
              </div>
            )}
            <input
              type="file"
              accept="application/pdf"
              required={!isEdicao}
              onChange={handleFileChange}
              className="w-full text-sm text-godoy-cream file:bg-godoy-gold file:text-godoy-black file:border-0 file:rounded-md file:px-4 file:py-2 file:text-xs file:mr-3 file:font-medium"
            />
            {uploadFile && (
              <p className="text-godoy-gold text-xs mt-2 flex items-center gap-1">
                <i className="ri-check-line" /> {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
            {isEdicao && <p className="text-godoy-lightgray/50 text-xs mt-2">Deixe em branco para manter o PDF atual.</p>}
          </div>

          {/* Dados do Produto */}
          <div className="p-5 bg-godoy-black/40 border border-godoy-gray/20 rounded-lg">
            <h4 className="text-godoy-gold text-sm font-semibold mb-4 flex items-center gap-2">
              <i className="ri-price-tag-3-line" /> Dados do Produto
            </h4>
            <div className="space-y-4">

              {/* Nome da música + Artista lado a lado */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-godoy-lightgray mb-1.5">Nome da Música *</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Insira o nome da música"
                    className="w-full px-3 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-godoy-lightgray mb-1.5">Artista / Banda *</label>
                  <input
                    required
                    value={form.artista}
                    onChange={(e) => setForm({ ...form, artista: e.target.value })}
                    placeholder="Insira o nome do Artista/Banda"
                    list="artistas-existentes"
                    className="w-full px-3 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
                  />
                  {/* Sugestão de artistas já cadastrados */}
                  <datalist id="artistas-existentes">
                    {artistas.map((a) => <option key={a} value={a} />)}
                  </datalist>
                </div>
              </div>

              <div>
                <label className="block text-xs text-godoy-lightgray mb-1.5">Álbum</label>
                <input
                  value={form.album}
                  onChange={(e) => setForm({ ...form, album: e.target.value })}
                  placeholder="Insira o nome do Álbum"
                  className="w-full px-3 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
                />
              </div>

              <div>
                <label className="block text-xs text-godoy-lightgray mb-1.5">Descrição</label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Descreva o arranjo, instrumentos, dificuldade..."
                  className="w-full px-3 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-godoy-lightgray mb-1.5">Preço (R$) *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="29.90"
                    className="w-full px-3 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-godoy-lightgray mb-1.5">Categoria</label>
                  <select
                    value={form.category_id}
                    onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                    className="w-full px-3 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none"
                  >
                    <option value="">Sem categoria</option>
                    {categories.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-godoy-lightgray mb-1.5">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none"
                  >
                    <option value="active">Ativo</option>
                    <option value="draft">Rascunho</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-godoy-lightgray mb-1.5">Moeda</label>
                  <input
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full px-3 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.discount_enabled}
                    onChange={(e) => setForm({ ...form, discount_enabled: e.target.checked })}
                    className="w-4 h-4 accent-godoy-gold"
                  />
                  <span className="text-sm text-godoy-cream">Ativar preço promocional</span>
                </label>
                {form.discount_enabled && (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Preço com desconto"
                    value={form.discount_price}
                    onChange={(e) => setForm({ ...form, discount_price: e.target.value })}
                    className="flex-1 px-3 py-2 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 bg-godoy-gold text-godoy-black font-bold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-godoy-black border-t-transparent rounded-full animate-spin" />
                  {uploading ? "Enviando PDF..." : "Salvando..."}
                </>
              ) : (
                <>
                  <i className={isEdicao ? "ri-save-line" : "ri-upload-cloud-2-line"} />
                  {isEdicao ? "Salvar Alterações" : "Publicar Partitura"}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => { setModo("lista"); resetForm(); setEditing(null); }}
              className="px-6 py-3 bg-godoy-gray/30 text-godoy-cream rounded-md hover:bg-godoy-gray/50 transition-all text-sm"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ===== LISTA AGRUPADA POR ARTISTA =====
  return (
    <div>
      {mensagem && <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-emerald-400 text-sm">{mensagem}</div>}
      {erro && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">{erro}</div>}

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <input
          type="text"
          placeholder="Buscar por nome ou artista..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
        />
        {artistas.length > 0 && (
          <select
            value={filterArtista}
            onChange={(e) => setFilterArtista(e.target.value)}
            className="px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none"
          >
            <option value="">Todos os artistas</option>
            {artistas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2.5 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50 appearance-none"
        >
          <option value="">Todos os status</option>
          <option value="active">Ativo</option>
          <option value="draft">Rascunho</option>
          <option value="inactive">Inativo</option>
        </select>
        <button
          onClick={openNovo}
          className="px-5 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap"
        >
          <i className="ri-add-line mr-1" /> Nova Partitura
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <i className="ri-file-music-line text-4xl text-godoy-lightgray/30 mb-3" />
          <p className="text-godoy-lightgray text-sm">Nenhuma partitura encontrada.</p>
          <button onClick={openNovo} className="mt-4 px-4 py-2 bg-godoy-gold/10 text-godoy-gold rounded-md text-sm hover:bg-godoy-gold/20 transition-all">
            Publicar primeira partitura
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(agrupados).sort(([a], [b]) => a.localeCompare(b)).map(([artista, items]) => (
            <div key={artista}>
              {/* Cabeçalho do artista */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-godoy-gold/10 border border-godoy-gold/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-music-line text-godoy-gold text-sm" />
                </div>
                <h4 className="font-serif text-base font-semibold text-godoy-gold">{artista}</h4>
                <span className="text-godoy-lightgray/50 text-xs">{items.length} partitura{items.length !== 1 ? "s" : ""}</span>
                <div className="flex-1 h-px bg-godoy-gray/20" />
              </div>

              {/* Tabela das partituras do artista */}
              <div className="overflow-x-auto rounded-lg border border-godoy-gray/20">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-godoy-gray/20 text-godoy-lightgray text-left bg-godoy-black/30">
                      <th className="pb-3 pt-3 px-4 font-medium">Nome</th>
                      <th className="pb-3 pt-3 pr-4 font-medium hidden md:table-cell">Álbum</th>
                      <th className="pb-3 pt-3 pr-4 font-medium">Preço</th>
                      <th className="pb-3 pt-3 pr-4 font-medium hidden md:table-cell">PDF</th>
                      <th className="pb-3 pt-3 pr-4 font-medium">Status</th>
                      <th className="pb-3 pt-3 pr-4 font-medium text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p.id} className="border-b border-godoy-gray/10 last:border-0 hover:bg-godoy-black/20 transition-colors">
                        <td className="py-3 px-4 text-godoy-cream font-medium text-sm truncate max-w-xs" title={p.name}>{p.name}</td>
                        <td className="py-3 pr-4 text-godoy-lightgray text-xs hidden md:table-cell">{p.album || "—"}</td>
                        <td className="py-3 pr-4 text-godoy-cream text-xs">
                          {formatPrice(p.price)}
                          {p.discount_enabled && p.discount_price != null && (
                            <span className="ml-1 text-emerald-400 text-xs">→ {formatPrice(p.discount_price)}</span>
                          )}
                        </td>
                        <td className="py-3 pr-4 hidden md:table-cell">
                          {p.pdf_url ? (
                            <a href={p.pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 flex items-center gap-1 hover:underline">
                              <i className="ri-file-pdf-line" /> Ver PDF
                            </a>
                          ) : (
                            <span className="text-xs text-red-400/70"><i className="ri-error-warning-line mr-1" />Sem PDF</span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${p.status === "active" ? "bg-emerald-500/10 text-emerald-400" : p.status === "inactive" ? "bg-red-500/10 text-red-400" : "bg-godoy-gold/10 text-godoy-gold"}`}>
                            {p.status === "active" ? "Ativo" : p.status === "inactive" ? "Inativo" : "Rascunho"}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => openEditar(p)} className="px-3 py-1.5 bg-godoy-gold/10 text-godoy-gold rounded-md hover:bg-godoy-gold/20 transition-all text-xs whitespace-nowrap">
                              <i className="ri-edit-line mr-1" />Editar
                            </button>
                            <button onClick={() => handleDelete(p.id)} className="px-3 py-1.5 bg-red-500/10 text-red-400 rounded-md hover:bg-red-500/20 transition-all text-xs whitespace-nowrap">
                              <i className="ri-delete-bin-line mr-1" />Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}